import SearchIcon from "@mui/icons-material/Search";
import {
	Grid,
	IconButton,
	InputBase,
	Skeleton,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	TableSortLabel, // <-- import this
	Typography,
} from "@mui/material";
import { fetchCustomerDocuments } from "actions/customerActions";
import { DownloadableDocument } from "components/Common/DownloadableDocument";
import { ListFilter } from "components/Common/ListFilter";
import { TruncatableTypography } from "components/Common/TruncateableTypography";
import dayjs from "dayjs";
import { selectCustomerDownloadStatuses, selectCustomerFiles } from "features/customer";
import { useApiOnceWithParams, useAppSelector } from "hooks/hooks";
import ReactElement, { useState } from "react";
import { ALL_DOCUMENT_TYPES_QUERY } from "utilities/constants/constants";
import styles from "./CompanyDocuments.module.scss";

// You may need this type or an equivalent, depending on your doc structure
interface DocumentData {
	id: string;
	title: string;
	pageName: string;
	lastModified: string;
	fileType: string;
}

// Basic helpers for sorting:
type Order = "asc" | "desc";

function descendingComparator<T>(a: T, b: T, orderBy: keyof T) {
	if (b[orderBy] < a[orderBy]) {
		return -1;
	}
	if (b[orderBy] > a[orderBy]) {
		return 1;
	}
	return 0;
}

function getComparator<Key extends keyof any>(
	order: Order,
	orderBy: Key,
): (a: { [key in Key]: any }, b: { [key in Key]: any }) => number {
	return order === "desc"
		? (a, b) => descendingComparator(a, b, orderBy)
		: (a, b) => -descendingComparator(a, b, orderBy);
}

function stableSort<T>(array: T[], comparator: (a: T, b: T) => number) {
	const stabilizedThis = array.map((el, index) => [el, index] as [T, number]);
	stabilizedThis.sort((a, b) => {
		const order = comparator(a[0], b[0]);
		if (order !== 0) {
			return order;
		}
		// If items are equal, use original index to keep stable sort
		return a[1] - b[1];
	});
	return stabilizedThis.map((el) => el[0]);
}

export const CompanyDocuments = () => {
	const [searchTerm, setSearchTerm] = useState("");
	const [filterTypes, setFilterTypes] = useState<string[]>([]);

	// Sorting states
	const [order, setOrder] = useState<Order>("desc");
	const [orderBy, setOrderBy] = useState<keyof DocumentData>("lastModified");

	const customerFileState = useAppSelector(selectCustomerFiles);

	useApiOnceWithParams(fetchCustomerDocuments, customerFileState, {
		pageName: ALL_DOCUMENT_TYPES_QUERY,
	});

	const filteredDocuments = customerFileState.documents.filter((doc) => {
		return (
			doc.title.toLowerCase().includes(searchTerm.toLowerCase()) &&
			(filterTypes.length === 0 || filterTypes.includes(doc.pageName))
		);
	});

	// Sort the filtered documents
	const sortedDocuments = stableSort(filteredDocuments, getComparator(order, orderBy));

	const handleSearchChange = (event: ReactElement.ChangeEvent<HTMLInputElement>) => {
		setSearchTerm(event.target.value);
	};

	// Handle a click on one of the header cells
	const handleRequestSort = (
		event: ReactElement.MouseEvent<unknown>,
		property: keyof DocumentData,
	) => {
		const isAsc = orderBy === property && order === "asc";
		setOrder(isAsc ? "desc" : "asc");
		setOrderBy(property);
	};

	return (
		<Grid container className={styles.container}>
			<Grid item xs={12}>
				<Typography variant="h3" fontWeight={500}>
					Documents
				</Typography>
				<Grid item xs={12} className={styles.searchAndFilterContainer}>
					<div className={styles.inputRoot}>
						<IconButton disabled aria-label="search">
							<SearchIcon />
						</IconButton>
						<InputBase
							value={searchTerm}
							className={styles.input}
							placeholder="Search documents"
							inputProps={{ "aria-label": "search for documents" }}
							onChange={handleSearchChange}
						/>
					</div>
					<ListFilter
						id="document-type-filter"
						filterValues={filterTypes}
						filterOptions={customerFileState.documentTypes ?? []}
						updateFilter={setFilterTypes}
						noneSelectedLabel="All document types"
					/>
				</Grid>
				<TableContainer className={styles.tableContainer}>
					<Table aria-label="company documents">
						<TableHead>
							<TableRow>
								{/* Document Name */}
								<TableCell sortDirection={orderBy === "title" ? order : false}>
									<TableSortLabel
										active={orderBy === "title"}
										direction={orderBy === "title" ? order : "asc"}
										onClick={(e) => handleRequestSort(e, "title")}
										// hideSortIcon // <-- Uncomment to remove the arrow icon
									>
										Document name
									</TableSortLabel>
								</TableCell>

								{/* Document Type */}
								<TableCell sortDirection={orderBy === "pageName" ? order : false}>
									<TableSortLabel
										active={orderBy === "pageName"}
										direction={orderBy === "pageName" ? order : "asc"}
										onClick={(e) => handleRequestSort(e, "pageName")}
										// hideSortIcon
									>
										Document type
									</TableSortLabel>
								</TableCell>

								{/* Date */}
								<TableCell
									sortDirection={orderBy === "lastModified" ? order : false}
								>
									<TableSortLabel
										active={orderBy === "lastModified"}
										direction={orderBy === "lastModified" ? order : "asc"}
										onClick={(e) => handleRequestSort(e, "lastModified")}
										// hideSortIcon
									>
										Date
									</TableSortLabel>
								</TableCell>

								{/* Actions (typically not sorted) */}
								<TableCell>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{customerFileState.isLoading ? (
								new Array(5).map((_, index) => (
									<TableRow key={index}>
										<TableCell>
											<Skeleton width="100%" />
										</TableCell>
										<TableCell>
											<Skeleton width="100%" />
										</TableCell>
										<TableCell>
											<Skeleton width="100%" />
										</TableCell>
										<TableCell>
											<Skeleton width="100%" />
										</TableCell>
									</TableRow>
								))
							) : sortedDocuments.length > 0 ? (
								sortedDocuments.map((doc) => {
									return (
										<TableRow key={doc.id} className={styles.tableRow}>
											<TableCell>
												<TruncatableTypography maxCharLength={50}>
													{doc.title}
												</TruncatableTypography>
											</TableCell>
											<TableCell>
												<Grid className={styles.defaultLabel}>
													{doc.pageName}
												</Grid>
											</TableCell>
											<TableCell>
												{dayjs(doc.lastModified).format("DD.MM.YYYY")}
											</TableCell>
											<TableCell>
												<DownloadableDocument
													id={doc.id}
													documentType={doc.pageName}
													documentTitle="Download"
													selector={selectCustomerDownloadStatuses}
													fileType={doc.fileType}
												/>
											</TableCell>
										</TableRow>
									);
								})
							) : (
								<TableRow>
									<TableCell colSpan={4} align="center">
										No documents found
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
			</Grid>
		</Grid>
	);
};
